import { useEffect, useRef } from 'react';

/**
 * Refetch when the tab comes back to the foreground.
 *
 * The grocery list has done this inline since it was built, and it is the behaviour worth having
 * everywhere: leave a tab open, come back later, and see current data instead of whatever was loaded
 * hours ago. Deliberately only on focus — no polling, no sockets.
 *
 * Fetch silently. The user didn't ask for this, so a spinner over data they are already looking at
 * reads as a glitch: pass `{ background: true }` to the load action, which skips the *_REQUEST
 * dispatch (the thing that sets `loading` and, in most reducers, drops the list) and swallows a
 * failure rather than replacing good data with an error.
 *
 * @param {Function} refetch Called when the tab becomes visible again.
 * @param {boolean} enabled  Pass false to stay idle (e.g. while logged out).
 */
const useRefetchOnFocus = (refetch, enabled = true) => {
  // Kept in a ref so callers can pass an inline arrow without re-binding the listener every render
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    if (!enabled) return undefined;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetchRef.current();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled]);
};

export default useRefetchOnFocus;
